import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { BACKGROUND_REQUEST } from './background-request.token';
import { inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
import { AuthResponse } from '../interfaces/auth.interfaces';
import { Router } from '@angular/router';
import { AuthStateService } from '../services/auth/auth-status.service';
import { TokenExpirationMonitorService } from '../services/auth/token-expiration-monitor.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export function addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({
    setHeaders: {
      'Authorization': `Bearer ${token}`
    }
  });
}

export function isPagaloRequest(request: HttpRequest<any>): boolean {
  return request.url.includes('api.pagalodev.com') || request.url.includes('api.pagalo.co');
}

export function isPublicAuthRequest(request: HttpRequest<any>): boolean {
  return request.url.includes('/auth/validate-recover-token') ||
    request.url.includes('/auth/generate-recover-token') ||
    request.url.includes('/auth/login') ||
    request.url.includes('/auth/sign-up/');
}

export function isRefreshRequest(request: HttpRequest<any>): boolean {
  return request.url.includes('/auth/refresh');
}

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
  authStateService: AuthStateService,
  tokenMonitor: TokenExpirationMonitorService
): Observable<HttpEvent<any>> {
  if (!isRefreshing) {
    isRefreshing = true;
    authStateService.setRefreshing(true);
    authStateService.setRefreshFailed(false);
    refreshTokenSubject.next(null);


    return authService.refreshToken().pipe(
      switchMap((authResponse: AuthResponse) => {
        isRefreshing = false;
        authStateService.setRefreshing(false);
        authStateService.setRefreshFailed(false);
        refreshTokenSubject.next(authResponse.access_token);
        tokenMonitor.notifyTokenRefreshed();
        return next(addTokenToRequest(request, authResponse.access_token));
      }),
      catchError((err) => {
        isRefreshing = false;
        authStateService.setRefreshing(false);
        authStateService.setRefreshFailed(true);

        if (!authService.isLoggingOut()) {
          const reason = err?.headers?.get('X-Session-Expired-Reason') || err?.error?.expiredReason || '';
          authService.forceLogoutWithReason(reason);
        }

        return throwError(() => new HttpErrorResponse({
          error: { message: 'Sesión expirada', sessionExpired: true },
          status: 401,
          statusText: 'Unauthorized - Session Expired'
        }));
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(newAccessToken => {
        return next(addTokenToRequest(request, newAccessToken!));
      })
    );
  }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const authStateService = inject(AuthStateService);
  const tokenMonitor = inject(TokenExpirationMonitorService);

  if (isPagaloRequest(req)) {
    return next(req);
  }

  const accessToken = authService.getAccessToken();
  if (accessToken) {
    req = addTokenToRequest(req, accessToken);
  }

  if (!isPagaloRequest(req) && !isPublicAuthRequest(req) && !isRefreshRequest(req) && !req.context.get(BACKGROUND_REQUEST)) {
    tokenMonitor.resetIdleTimer();
  }

  return next(req).pipe(
    catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401 && !isPublicAuthRequest(req)) {
        return handle401Error(req, next, authService, router, authStateService, tokenMonitor);
      }
      return throwError(() => error);
    })
  );
};